import { useCallback, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useCreatePlaceCommentMutation } from '@cobu/api-client'
import { isCommentModeration, type ModerationFlaggedTerm } from '@cobu/types'
import { analyticsService, AnalyticsEvent } from '@cobu/analytics'
import { RootState } from '../store'
import { Strings } from '../constants/Strings'
import { URLS } from '../constants'
import { logger } from '../utils/logger'

const strings = Strings.get()

type ModerationType = 'PROFANITY' | 'SENTIMENT'

/**
 * Compose hook for a place's "neighbor chat" comment. Flat only — the backend
 * has no threading on place comments (replies live on the Feed), so there's no
 * replyingTo/parent. Attribution is derived server-side from the auth token.
 *
 * Place comments run through the same moderation stack as the Feed: a blocked
 * comment comes back as a 200 carrying `moderationType` instead of a comment, so
 * posting isn't done until that's been checked. PROFANITY is a hard stop (edit
 * or read the code of conduct); SENTIMENT offers "Send Anyway", which reposts
 * with userDecision and lets the server file it hidden for manual review.
 *
 * Unlike the Feed this sends no `editingReport` — the place endpoint doesn't
 * accept one, so there's no running session transcript to accumulate.
 */
export function useComposePlaceComment() {
  const params = useParams<{ id: string }>()
  const navigate = useNavigate()
  const placeId = params.id || ''

  const user = useSelector((state: RootState) => state.auth.user)
  const [contents, setContents] = useState('')
  const [flaggedTerms, setFlaggedTerms] = useState<ModerationFlaggedTerm[]>([])
  const [moderationAlert, setModerationAlert] = useState<{
    open: boolean
    type: ModerationType | null
    confidence?: number
  }>({ open: false, type: null })

  const createComment = useCreatePlaceCommentMutation(placeId)

  const closeModerationAlert = useCallback(() => {
    setModerationAlert({ open: false, type: null })
  }, [])

  // Clearing the highlight as soon as they start editing keeps stale offsets
  // from pointing at the wrong characters in the new text.
  const handleSetContents = useCallback((value: string) => {
    setContents(value)
    setFlaggedTerms((prev) => (prev.length > 0 ? [] : prev))
  }, [])

  const submitComment = useCallback(
    async (userDecision?: 'SEND_ANYWAY') => {
      const result = await createComment.mutateAsync({
        input: { place: placeId, contents: contents.trim(), userDecision },
      })

      if (isCommentModeration(result)) {
        setFlaggedTerms(result.flaggedTerms ?? [])
        if (result.moderationType === 'PROFANITY') {
          analyticsService.trackEvent(AnalyticsEvent.BLOCKED_PROFANITY, {
            email_address: user?.email,
            first_name: user?.firstName,
            last_name: user?.lastName,
            building_name: user?.building?.name,
            profanity_detected: contents,
          })
        }
        setModerationAlert({
          open: true,
          type: result.moderationType,
          confidence: result.confidence,
        })
        return
      }

      // Posted — including the auto-hide case, where the server created the
      // comment hidden and it simply won't come back in the list.
      setFlaggedTerms([])
      navigate(-1)
    },
    [contents, createComment, placeId, navigate, user]
  )

  const handlePost = useCallback(async () => {
    if (!contents.trim()) return
    try {
      await submitComment()
    } catch (e) {
      logger.error('[ComposePlaceComment] failed to post comment', e)
    }
  }, [contents, submitComment])

  const handleModerationDecision = useCallback(
    async (decision: string) => {
      switch (decision) {
        case strings.discussion.moderationAlerts.sentiment.options.sendAnyway:
          closeModerationAlert()
          try {
            await submitComment('SEND_ANYWAY')
          } catch (e) {
            logger.error('[ComposePlaceComment] failed to post comment', e)
          }
          break
        case strings.discussion.moderationAlerts.profanity.options.editPost:
        case strings.discussion.moderationAlerts.sentiment.options.editPost:
          closeModerationAlert()
          break
        case strings.discussion.moderationAlerts.profanity.options.viewCodeOfConduct:
        case strings.discussion.moderationAlerts.sentiment.options.viewCodeOfConduct:
          closeModerationAlert()
          if (URLS.CODE_OF_CONDUCT) {
            // Opened in a new window rather than an iframe — Intercom blocks framing.
            window.open(URLS.CODE_OF_CONDUCT, '_blank', 'noopener,noreferrer')
          } else {
            logger.error('Missing CODE_OF_CONDUCT URL')
          }
          break
      }
    },
    [closeModerationAlert, submitComment]
  )

  return {
    user,
    contents,
    setContents: handleSetContents,
    isSubmitting: createComment.isPending,
    handlePost,
    flaggedTerms,
    moderationAlert,
    closeModerationAlert,
    handleModerationDecision,
  }
}
